import React, { useCallback } from 'react';
import {
  Avatar,
  AvatarGroup,
  Box,
  ListItemButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { WorkspacePremium } from '@mui/icons-material';

type GangMember = {
  userId: string;
  givenName?: string | null;
  familyName?: string | null;
  isLeader: boolean;
};

export interface GangListItemProps {
  id: string;
  name: string;
  members: GangMember[];
  onRowClick?: (id: string) => void;
}

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 55%)`;
};

const getGangInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getMemberInitials = (
  givenName?: string | null,
  familyName?: string | null
): string => {
  const first = (givenName ?? '').trim().charAt(0);
  const last = (familyName ?? '').trim().charAt(0);
  return first || last ? `${first}${last}`.toUpperCase() : 'U';
};

const getFullName = (member: GangMember): string => {
  const given = (member.givenName ?? '').trim();
  const family = (member.familyName ?? '').trim();
  return given || family ? `${given} ${family}`.trim() : 'Unknown';
};

const sortMembersByLeadership = (members: GangMember[]): GangMember[] => {
  return [...members].sort((a, b) =>
    a.isLeader === b.isLeader ? 0 : a.isLeader ? -1 : 1
  );
};

export const GangListItem = React.memo(function GangListItem({
  id,
  name,
  members,
  onRowClick,
}: GangListItemProps) {
  const handleClick = useCallback(() => {
    onRowClick?.(id);
  }, [onRowClick, id]);

  const sortedMembers = sortMembersByLeadership(members);
  const leaderCount = members.filter(m => m.isLeader).length;

  return (
    <ListItemButton
      onClick={handleClick}
      data-testid="gang-row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.75rem',
          fontWeight: 600,
          bgcolor: stringToColor(name),
          flexShrink: 0,
        }}
      >
        {getGangInitials(name)}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          data-testid="gang-name"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {members.length} {members.length === 1 ? 'member' : 'members'} ·{' '}
          {leaderCount} {leaderCount === 1 ? 'leader' : 'leaders'}
        </Typography>
      </Box>

      {/* AvatarGroup — hidden on mobile */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <AvatarGroup
          max={5}
          sx={{
            '& .MuiAvatar-root': {
              width: 28,
              height: 28,
              fontSize: 12,
            },
          }}
        >
          {sortedMembers.map(member => (
            <Tooltip key={member.userId} title={getFullName(member)} arrow>
              <Avatar
                sx={{
                  bgcolor: stringToColor(getFullName(member)),
                  color: '#fff',
                  overflow: 'visible',
                  position: 'relative',
                }}
              >
                {getMemberInitials(member.givenName, member.familyName)}
                {member.isLeader && (
                  <WorkspacePremium
                    data-testid={`gang-member-${member.userId}-leader-badge`}
                    sx={theme => ({
                      position: 'absolute',
                      top: -3,
                      right: -3,
                      fontSize: 14,
                      color: theme.palette.warning.main,
                      zIndex: 1,
                    })}
                  />
                )}
              </Avatar>
            </Tooltip>
          ))}
        </AvatarGroup>
      </Box>
    </ListItemButton>
  );
});
